import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MainContent from '../components/MainContent';
import AssignDialog from '../components/forms/AssignDialog';
import {
  CARD,
  FormStatusBadge,
  INPUT,
  PRIMARY,
  SECONDARY,
  ProgressBar,
  formatDue,
} from '../components/forms/formsText';
import { useAllCohorts } from '../hooks/useStudentRankings';
import {
  PAGE_SIZE,
  formsError,
  useAssignments,
} from '../hooks/useFormAssignments';

const FORMS_SITE_BUILDER =
  'https://forms.powerlearnprojectafrica.org/staff/forms';

// Forms from forms.powerlearnprojectafrica.org assigned to cohorts.
export default function FormAssignments() {
  const navigate = useNavigate();
  const [assigning, setAssigning] = useState(false);
  const [filters, setFilters] = useState({ status: 'active' });
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const { data: cohortsData } = useAllCohorts();
  const cohorts = cohortsData?.data?.cohorts ?? [];

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => ({ ...f, q: search.trim() }));
      setOffset(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error } = useAssignments(filters, offset);
  const rows = data?.assignments ?? [];
  const total = data?.total ?? 0;
  const set = (changes) => {
    setFilters((f) => ({ ...f, ...changes }));
    setOffset(0);
  };

  return (
    <MainContent full>
      <div className="space-y-5 pb-10">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Forms</h1>
            <p className="text-xs text-gray-500">
              Forms from forms.powerlearnprojectafrica.org assigned to cohorts.
              Students answer them in their dashboard.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Until forms can be built here, build them on the forms site. */}
            <a
              href={FORMS_SITE_BUILDER}
              target="_blank"
              rel="noopener noreferrer"
              className={`${SECONDARY} inline-flex items-center gap-1`}
            >
              Create a form
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </a>
            <button
              type="button"
              onClick={() => setAssigning(true)}
              className={`${PRIMARY} inline-flex items-center gap-1`}
            >
              <AddIcon fontSize="small" />
              Assign a form
            </button>
          </div>
        </header>

        <div className={`${CARD} flex flex-wrap items-center gap-3 p-4`}>
          <select
            aria-label="Cohort"
            value={filters.cohortId || ''}
            onChange={(e) => set({ cohortId: e.target.value })}
            className={`${INPUT} max-w-xs`}
          >
            <option value="">All cohorts</option>
            {cohorts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Status"
            value={filters.status || ''}
            onChange={(e) => set({ status: e.target.value })}
            className={INPUT}
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="">All</option>
          </select>
          <input
            type="search"
            aria-label="Search forms"
            placeholder="Search title or form"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${INPUT} w-60`}
          />
        </div>

        {isLoading && (
          <div className={`${CARD} h-48 animate-pulse`} aria-label="Loading" />
        )}
        {error && (
          <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
            {formsError(error, "We couldn't load the forms.")}
          </div>
        )}
        {data && !error && total === 0 && (
          <div className={`${CARD} p-10 text-center text-sm text-gray-600`}>
            No forms assigned yet.
          </div>
        )}
        {data && !error && total > 0 && (
          <section className={`${CARD} overflow-hidden`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 pl-5 pr-2 font-medium">Form</th>
                  <th className="px-2 py-2 font-medium">Cohort</th>
                  <th className="w-56 px-2 py-2 font-medium">Responses</th>
                  <th className="px-2 py-2 font-medium">Due</th>
                  <th className="py-2 pl-2 pr-5" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/forms/${a.id}`)}
                    className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    <td className="py-2.5 pl-5 pr-2">
                      <span className="text-gray-900">{a.title}</span>{' '}
                      <FormStatusBadge status={a.form_status} />
                      {a.status === 'archived' && (
                        <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                          Archived
                        </span>
                      )}
                      {a.title !== a.form_title && (
                        <span className="block text-xs text-gray-500">
                          {a.form_title}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-gray-700">
                      {a.cohort?.name}
                    </td>
                    <td className="px-2 py-2.5">
                      <ProgressBar counts={a.counts} />
                    </td>
                    <td className="whitespace-nowrap px-2 py-2.5 text-xs text-gray-600">
                      {formatDue(a.due_at)}
                    </td>
                    <td className="py-2.5 pl-2 pr-5 text-right text-gray-400">
                      <ChevronRightIcon fontSize="small" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2 text-xs text-gray-500">
                <span>
                  {offset + 1}–{Math.min(offset + rows.length, total)} of{' '}
                  {total}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={offset === 0}
                    onClick={() => setOffset(offset - PAGE_SIZE)}
                    className={SECONDARY}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    className={SECONDARY}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
      <AssignDialog open={assigning} onClose={() => setAssigning(false)} />
    </MainContent>
  );
}
